// src/services/notifications.js
// Web Notifications API helper. Notif del SO cuando llega un evento crítico
// (escalado de Atención a Clientes). Funciona incluso si el navegador está
// minimizado o el agente está en otra app — siempre que la pestaña del
// sistema esté abierta en alguna parte.
//
// Si el permiso fue denegado, los callers siguen funcionando (los toasts +
// ping + badge cubren el caso degradado).

const LS_KEY_ASKED = 'caprepa.notifAsked'; // si ya preguntamos al user

function isSupported() {
    return typeof window !== 'undefined' && 'Notification' in window;
}

/** ¿En qué estado está el permiso? 'granted' | 'denied' | 'default' | 'unsupported' */
export function permissionState() {
    if (!isSupported()) return 'unsupported';
    return Notification.permission;
}

/** Pide permiso al user. Si ya lo concedió/denegó, no vuelve a preguntar.
 *  Devuelve la decision final. */
export async function requestPermission() {
    if (!isSupported()) return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    try {
        const result = await Notification.requestPermission();
        try { localStorage.setItem(LS_KEY_ASKED, '1'); } catch {}
        return result;
    } catch {
        return 'denied';
    }
}

/** ¿Ya le preguntamos al user alguna vez? Para no hostigar. */
export function hasBeenAsked() {
    if (!isSupported()) return false;
    if (Notification.permission !== 'default') return true; // ya decidió
    try { return localStorage.getItem(LS_KEY_ASKED) === '1'; } catch { return false; }
}

/** Muestra una notificación del SO. Si el permiso no está, retorna false sin
 *  romper nada. Opciones:
 *    title    string (obligatorio)
 *    body     string (texto secundario)
 *    tag      string — agrupa notifs (la siguiente con mismo tag reemplaza la anterior)
 *    icon     string url — opcional, default usa el favicon
 *    requireInteraction bool — si true no se autocierra
 *    onClick  function — qué hacer cuando el user hace click (default: focus window)
 */
export function showNotification({ title, body, tag, icon, requireInteraction, onClick } = {}) {
    if (!isSupported() || Notification.permission !== 'granted') return false;
    try {
        const n = new Notification(title, {
            body: body || '',
            tag: tag || undefined,
            icon: icon || '/caprepa.svg',
            requireInteraction: !!requireInteraction,
        });
        n.onclick = (ev) => {
            ev.preventDefault();
            try { window.focus(); } catch {}
            if (onClick) {
                try { onClick(ev); } catch {}
            }
            try { n.close(); } catch {}
        };
        // Auto-cerrar si no es interactivo (algunos navegadores no respetan
        // requireInteraction=false y dejan abierta indefinidamente).
        if (!requireInteraction) {
            setTimeout(() => { try { n.close(); } catch {} }, 8000);
        }
        return true;
    } catch (e) {
        // En iOS Safari y algunos contextos, Notification puede tirar. Silencioso.
        console.warn('[notifications] no se pudo mostrar:', e?.message);
        return false;
    }
}

/** ¿La pestaña está visible ahora? Útil para no spamear notifs cuando el
 *  user ya está mirando la app. */
export function isTabHidden() {
    return typeof document !== 'undefined' && document.hidden;
}
